export const locales = ["en", "ar"] as const;
export type Locale = (typeof locales)[number];

export function isLocale(value: string): value is Locale {
  return locales.includes(value as Locale);
}

export function directionForLocale(locale: Locale): "ltr" | "rtl" {
  return locale === "ar" ? "rtl" : "ltr";
}

const messages = {
  en: {
    product: "XcutIQ Proposal Builder",
    productShort: "Proposal Builder",
    productDescriptor: "Proposal intelligence",
    eyebrow: "proposal delivery",
    title: "From evidence to an authorized submission.",
    description:
      "Bring intake, pricing, bilingual authoring, human approvals, and traceable submission into one secure proposal workspace.",
    status: "Proposal workspace available",
    skip: "Skip to main content",
    switchLocale: "العربية",
    switchHref: "/ar",
    switchLanguage: "Switch language to Arabic",
    errorTitle: "The workspace could not be loaded",
    errorDescription: "No customer content or internal identifiers were exposed. Try the request again.",
    errorAction: "Try again",
    notFoundTitle: "Page not found",
    notFoundAction: "Return to the workspace",
    signInEyebrow: "Protected access",
    signInTitle: "Sign in to your tenant",
    signInDescription: "Use your authorized enterprise identity to continue.",
    signInStoryTitle: "Build with evidence. Submit with control.",
    signInStoryDescription:
      "Bring intake, solution, commercials, bilingual content, reviews, and approvals into one proposal workspace.",
    signInBenefitEvidence: "Evidence-linked intake and requirements",
    signInBenefitFinance: "Consistent pricing and margin review",
    signInBenefitApproval: "Human-owned approvals and submission",
    signInSecurity: "Sessions expire automatically, and sign-ins are recorded for security.",
    tenantLabel: "Tenant",
    emailLabel: "Email address",
    passwordLabel: "Password",
    signInAction: "Sign in",
    signInProgress: "Signing in",
    signInError: "Sign-in failed. Check your details or contact an administrator.",
    accessTitle: "Access review",
    accessDescription: "roles and active memberships for this tenant.",
    roleLabel: "Role",
    memberCountLabel: "Active members",
    noAccessTitle: "Access not available",
    logoutAction: "Sign out",
    shell: {
      primaryNavigation: "Primary navigation",
      openNavigation: "Open navigation",
      closeNavigation: "Close navigation",
      collapseNavigation: "Collapse navigation",
      expandNavigation: "Expand navigation",
      governedWorkspace: "Workspace",
      enterpriseWorkspace: "Enterprise proposal workspace",
      accountMenu: "Account menu",
      accountRole: "Authorized workspace user",
      navigationUnavailable: "No modules are available for the current role.",
      currentPage: "Current page",
    },
    navigation: {
      dashboard: "Dashboard",
      opportunities: "Opportunities",
      proposals: "Proposals",
      intake: "Intake",
      requirements: "Requirements",
      solution: "Solution",
      competitive: "Competitive strategy",
      templates: "Templates and brand",
      finance: "Finance",
      content: "Content",
      translations: "Translation and terminology",
      reviews: "Reviews and approvals",
      submissions: "Submissions",
      integrations: "Updates and services",
      knowledge: "Knowledge and catalogues",
      reports: "Reports",
      records: "Data lifecycle",
      aiProvider: "AI provider",
      administration: "Administration",
      interfaceFoundation: "Interface foundation",
    },
    workspace: {
      eyebrow: "Workspace overview",
      title: "Proposal delivery, ready for work.",
      description:
        "Manage proposal work across intake, solution, commercials, content, approvals, submission, and outcomes.",
      authority: "Access matched to your role",
      foundationStatus: "Your work",
      foundationTitle: "Authorized workspaces",
      foundationDescription: "Choose the area that matches your current proposal responsibility.",
      openFoundation: "Open workspace",
      openTask: "Open",
      noProposalTitle: "Choose where to begin",
      noProposalDescription: "Open an authorized workspace above, then choose the proposal or task you need to continue.",
      governedModules: "Modules",
      governedModulesDescription: "Your workspace shows the areas and actions available for your role.",
      bilingualTitle: "Arabic and English",
      bilingualDescription: "Direction, mixed content, dates, numbers, and currencies follow an explicit presentation policy.",
      accessibilityTitle: "Accessible by default",
      accessibilityDescription: "Landmarks, skip navigation, visible focus, live status, and non-color states are reusable foundations.",
      moduleBoundaryTitle: "Workspace unavailable",
      moduleBoundaryDescription: "This workspace is not available for your current route. Return to the navigation to continue with an authorized task.",
    },
    opportunities: {
      eyebrow: "Opportunity lifecycle",
      title: "Opportunities and proposal activity",
      description: "Stable identities, versions, readiness dimensions, and accountable state decisions from the domain record.",
      emptyTitle: "No opportunity yet",
      emptyDescription: "Create an opportunity to begin intake, qualification, planning, and proposal delivery.",
      opportunityColumn: "Opportunity",
      customerColumn: "Customer",
      proposalColumn: "Proposal",
      stateColumn: "Lifecycle state",
      versionColumn: "Version",
      updatedColumn: "Last decision",
      openActivity: "Open activity",
      detailEyebrow: "opportunity",
      customerLabel: "Customer",
      proposalLabel: "Proposal",
      deadlineLabel: "Deadline",
      noDeadline: "Not recorded",
      readinessTitle: "Independent readiness",
      readinessDescription: "Every mandatory dimension remains visible. A combined score cannot hide a blocker.",
      activityTitle: "Accountable activity timeline",
      activityDescription: "Chronological state, readiness, and snapshot decisions without protected document or commercial payloads.",
      noActivity: "No domain activity is available.",
      actorLabel: "Actor",
      reasonLabel: "Reason",
      noReason: "No transition reason required",
      versionLabel: "Record version",
      createdAction: "Opportunity created",
      transitionedAction: "Lifecycle transitioned",
      readinessAction: "Readiness updated",
      snapshotAction: "Fixed version created",
      policyAction: "Lifecycle policy activated",
      unknownAction: "domain action",
      dimensions: {
        solution: "Solution",
        commercial: "Commercial",
        content: "Content",
        compliance: "Compliance",
        language: "Language",
      },
      readinessStatuses: {
        not_started: "Not started",
        in_progress: "In progress",
        blocked: "Blocked",
        ready: "Ready",
      },
      states: {
        draft: "Draft",
        intake: "Intake",
        qualification: "Qualification",
        planning: "Planning",
        solutioning: "Solutioning",
        pricing: "Pricing",
        authoring: "Authoring",
        review: "Review",
        approval: "Approval",
        ready_to_submit: "Ready to submit",
        submitted: "Submitted",
        amended: "Amended",
        won: "Won",
        lost: "Lost",
        withdrawn: "Withdrawn",
        expired: "Expired",
        archived: "Archived",
      },
    },
    documents: {
      eyebrow: "evidence intake",
      title: "Proposal document register",
      description: "Upload customer and vendor evidence for safety checks. Every accepted file retains its version and audit history.",
      backToOpportunity: "Back to opportunity",
      uploadTitle: "Upload evidence",
      uploadDescription: "Files are quarantined, malware-scanned, type-detected, and policy-checked before access.",
      fileLabel: "Choose a file",
      dropHint: "Drop a file here or use the keyboard-accessible file selector.",
      categoryLabel: "Document category",
      sensitivityLabel: "Sensitivity",
      issuerLabel: "Issuer (optional)",
      effectiveDateLabel: "Effective date (optional)",
      replacementLabel: "Replacement / new version (optional)",
      newDocument: "Create a new document",
      uploadAction: "Upload securely",
      uploading: "Uploading to quarantine",
      uploadSuccess: "Upload accepted for processing.",
      uploadFailure: "The upload could not be accepted. Review the file and try again.",
      registerTitle: "Document register",
      emptyTitle: "No proposal evidence uploaded",
      emptyDescription: "Choose an authorized file to begin the evidence lifecycle.",
      documentColumn: "Document",
      categoryColumn: "Category",
      ownerColumn: "Owner",
      sensitivityColumn: "Sensitivity",
      statusColumn: "Processing status",
      versionColumn: "Version",
      actionsColumn: "Actions",
      previewAction: "Preview",
      downloadAction: "Download",
      retryAction: "Retry processing",
      activityAction: "View activity",
      refreshAction: "Refresh register",
      reviewReasonLabel: "Human review reason",
      promoteAction: "Approve and promote",
      rejectAction: "Reject file",
      actionFailure: "The action could not be completed.",
      noActivity: "No activity is available.",
      systemActor: "Automated service",
      versionPrefix: "Version",
      sizeLabel: "Size",
      reasonCodeLabel: "Reason code",
      intelligenceAction: "Review extraction",
      intelligenceTitle: "Evidence extraction review",
      intelligenceDescription: "Native text is preferred and OCR is used only when needed. Every result remains unverified until human disposition.",
      intelligenceLoading: "Loading extraction evidence",
      intelligenceEmpty: "Extraction has not started. Refresh after document processing completes.",
      intelligenceStatusLabel: "Extraction status",
      intelligenceStatuses: { not_started: "Not started", queued: "Queued", running: "Running", succeeded: "Completed", failed: "Retry scheduled", dead_letter: "Manual recovery required" },
      extractionModeLabel: "Method",
      languageLabel: "Detected language",
      classificationLabel: "Suggested classification",
      qualityLabel: "Quality score",
      confidenceLabel: "Confidence",
      runLabel: "Extraction run",
      unverifiedBannerTitle: "Unverified candidates",
      unverifiedBannerDescription: "Extraction and AI output cannot verify themselves or update proposal truth. Confirm consequential values against the cited source.",
      textEvidenceTitle: "Located text evidence",
      tablesTitle: "Extracted tables",
      candidatesTitle: "Structured candidates",
      candidateKindColumn: "Candidate type",
      candidateValueColumn: "Extracted value",
      sourceColumn: "Exact source",
      openSourceAction: "Open cited source",
      verificationColumn: "Verification",
      unverifiedStatus: "Unverified",
      noCandidates: "No structured candidates were found. This is not evidence that required data is complete.",
      injectionTitle: "Untrusted instruction detected",
      injectionDescription: "Instructions found inside the uploaded file were treated as document text and could not change access or processing rules.",
      aiBoundaryTitle: "AI processing status",
      aiManualFallback: "Manual fallback active",
      aiBoundaryDescription: "AI suggestions are read-only and require human review before use. They cannot approve work or change proposal state.",
      retryExtractionAction: "Create a new extraction version",
      retryExtractionReasonLabel: "Reason for re-extraction",
      retryExtractionReasonPlaceholder: "Explain the parser, OCR, or source-quality issue",
      sourcePage: "Page",
      sourceNotFound: "The exact source location is not present in this extraction version.",
      sourceSheet: "Sheet",
      sourceSlide: "Slide",
      sourceParagraph: "Paragraph",
      sourceLine: "Line",
      methodLabels: { native: "Native text", ocr: "OCR", hybrid: "Native + OCR" },
      languageLabels: { en: "English", ar: "Arabic", mixed: "Arabic and English", unknown: "Unknown" },
      categories: {
        rfp: "RFP", rfq: "RFQ", rfi: "RFI", questionnaire: "Questionnaire", clarification: "Clarification",
        meeting_outcome: "Meeting outcome", quotation: "Quotation", technical: "Technical", commercial: "Commercial",
        legal: "Legal", compliance: "Compliance", supporting_evidence: "Supporting evidence", other: "Other",
      },
      sensitivities: { internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
      statuses: {
        quarantined: "Quarantined", scanning: "Scanning", accepted: "Accepted", review_required: "Human review required",
        rejected: "Rejected", processing_failed: "Processing failed", dead_letter: "Manual recovery required",
      },
    },
    foundation: {
      eyebrow: "Internal component reference",
      title: "Bilingual interface foundation",
      description: "Production components adapted from the approved ResolveOps visual grammar for Proposal Builder workflows.",
      breadcrumbWorkspace: "Workspace",
      breadcrumbCurrent: "Interface foundation",
      examplesNotice: "The values below are sanitized interface examples, not proposal records or approvals.",
      actionsTitle: "Actions and feedback",
      primaryAction: "Primary action",
      secondaryAction: "Secondary action",
      openDialog: "Open review dialog",
      openDrawer: "Open evidence drawer",
      showNotification: "Show notification",
      successStatus: "Ready",
      warningStatus: "Needs review",
      errorStatus: "Blocked",
      infoBannerTitle: "Evidence remains authoritative",
      infoBannerDescription: "Interface summaries never replace source documents or approved business state.",
      warningBannerTitle: "Human review required",
      warningBannerDescription: "AI advice and extracted values remain unverified until an authorized person accepts them.",
      tabsLabel: "Foundation examples",
      overviewTab: "Overview",
      evidenceTab: "Evidence",
      activityTab: "Activity",
      tabOverviewCopy: "A stable workspace keeps identity, state, deadline, and readiness in context.",
      tabEvidenceCopy: "Sources remain reachable and visually distinct from advice and approval decisions.",
      tabActivityCopy: "Changes, comments, decisions, and audit events retain accountable ownership.",
      formTitle: "Localized form",
      proposalNameLabel: "Proposal name",
      proposalNameHint: "Use the customer-facing opportunity name.",
      proposalNamePlaceholder: "Regional service modernization",
      requiredError: "Enter a proposal name before continuing.",
      tableTitle: "Responsive requirements table",
      requirementColumn: "Requirement",
      sourceColumn: "Source",
      ownerColumn: "Owner",
      statusColumn: "Status",
      rowRequirement: "Provide bilingual service documentation",
      rowSource: "RFP § 4.2",
      rowOwner: "Solution team",
      rowStatus: "Needs review",
      mobileRecordLabel: "Requirement detail",
      emptyTitle: "No evidence attached",
      emptyDescription: "Upload a source document to begin evidence processing and review.",
      errorStateTitle: "Content unavailable",
      errorStateDescription: "The safe error state does not reveal customer content or internal identifiers.",
      loadingLabel: "Loading workspace content",
      dialogTitle: "Review required",
      dialogDescription: "Confirm that the example interaction preserves focus and keyboard operation.",
      dialogConfirm: "Confirm review",
      cancelAction: "Cancel",
      closeAction: "Close",
      drawerTitle: "Evidence context",
      drawerDescription: "Source access remains separate from AI advice and approval state.",
      notificationMessage: "Interface notification announced.",
      notificationRegion: "Notifications",
      proposalExampleLabel: "Interface example",
      proposalExampleName: "Regional Service Modernization",
      proposalExampleStatus: "Draft example",
      proposalExampleDeadline: "Submission deadline",
      proposalExampleOwner: "Proposal owner",
      proposalExampleOwnerValue: "Proposal team",
      proposalExampleVersion: "Active version",
      proposalExampleVersionValue: "Draft v1",
      proposalStagesLabel: "Proposal stages",
      stageIntake: "Intake",
      stageRequirements: "Requirements",
      stageSolution: "Solution",
      stageCommercials: "Commercials",
      stageContent: "Content",
      stageApprovals: "Approvals",
      stageSubmission: "Submission",
      blockersLabel: "Open blockers",
      blockersValue: "2 example blockers",
      advisorTitle: "Codex Proposal Intelligence Advisor",
      advisorStatus: "Advisory only",
      advisorDescription: "Advice is visually separated from facts, calculations, and approvals and always requires human disposition.",
      advisorAction: "Review advice boundary",
      mainTaskTitle: "Main task surface",
      mainTaskDescription: "Complete the next proposal task using the workspaces available for your role.",
    },
  },
  ar: {
    product: "منصة XcutIQ لبناء العروض",
    productShort: "منصة بناء العروض",
    productDescriptor: "ذكاء العروض",
    eyebrow: "إعداد عروض",
    title: "من الأدلة إلى عرض معتمد وجاهز للتقديم.",
    description:
      "أساس إنتاجي للاستقبال الذكي والتسعير المتسق والتحرير ثنائي اللغة والموافقات البشرية والتقديم القابل للتتبع.",
    status: "مساحة عمل العروض متاحة",
    skip: "انتقل إلى المحتوى الرئيسي",
    switchLocale: "English",
    switchHref: "/en",
    switchLanguage: "تغيير اللغة إلى الإنجليزية",
    errorTitle: "تعذر تحميل مساحة العمل",
    errorDescription: "لم يتم كشف أي محتوى للعميل أو معرفات داخلية. حاول تنفيذ الطلب مرة أخرى.",
    errorAction: "حاول مرة أخرى",
    notFoundTitle: "الصفحة غير موجودة",
    notFoundAction: "العودة إلى مساحة العمل",
    signInEyebrow: "وصول محمي",
    signInTitle: "تسجيل الدخول إلى مؤسستك",
    signInDescription: "استخدم هويتك المؤسسية المصرح بها للمتابعة.",
    signInStoryTitle: "ابنِ العرض بالأدلة. وقدّمه بضوابط واضحة.",
    signInStoryDescription:
      "اجمع الاستقبال والحل والجوانب التجارية والمحتوى ثنائي اللغة والمراجعات والموافقات في مساحة عروض واحدة.",
    signInBenefitEvidence: "استقبال ومتطلبات مرتبطة بالأدلة",
    signInBenefitFinance: "تسعير ومراجعة هامش بحسابات متسقة",
    signInBenefitApproval: "موافقات وتقديم بمسؤولية بشرية",
    signInSecurity: "تنتهي الجلسات تلقائياً، وتُسجل عمليات الدخول لحماية الحساب.",
    tenantLabel: "المؤسسة",
    emailLabel: "البريد الإلكتروني",
    passwordLabel: "كلمة المرور",
    signInAction: "تسجيل الدخول",
    signInProgress: "جارٍ تسجيل الدخول",
    signInError: "تعذر تسجيل الدخول. تحقق من البيانات أو تواصل مع المسؤول.",
    accessTitle: "مراجعة الصلاحيات",
    accessDescription: "الأدوار والعضويات النشطة لهذه المؤسسة.",
    roleLabel: "الدور",
    memberCountLabel: "الأعضاء النشطون",
    noAccessTitle: "الصلاحية غير متاحة",
    logoutAction: "تسجيل الخروج",
    shell: {
      primaryNavigation: "التنقل الرئيسي",
      openNavigation: "فتح قائمة التنقل",
      closeNavigation: "إغلاق قائمة التنقل",
      collapseNavigation: "طي قائمة التنقل",
      expandNavigation: "توسيع قائمة التنقل",
      governedWorkspace: "مساحة العمل",
      enterpriseWorkspace: "مساحة مؤسسية لإعداد العروض",
      accountMenu: "قائمة الحساب",
      accountRole: "مستخدم مصرح له في مساحة العمل",
      navigationUnavailable: "لا توجد وحدات متاحة للدور الحالي.",
      currentPage: "الصفحة الحالية",
    },
    navigation: {
      dashboard: "لوحة المتابعة",
      opportunities: "الفرص",
      proposals: "العروض",
      intake: "الاستقبال",
      requirements: "المتطلبات",
      solution: "الحل",
      competitive: "الاستراتيجية التنافسية",
      templates: "القوالب والهوية",
      finance: "المالية",
      content: "المحتوى",
      translations: "الترجمة والمصطلحات",
      reviews: "المراجعات والموافقات",
      submissions: "التقديمات",
      integrations: "التحديثات والخدمات",
      knowledge: "المعرفة والكتالوجات",
      reports: "التقارير",
      records: "دورة حياة البيانات",
      aiProvider: "مزود الذكاء الاصطناعي",
      administration: "الإدارة",
      interfaceFoundation: "أساس الواجهة",
    },
    workspace: {
      eyebrow: "نظرة عامة على مساحة العمل",
      title: "بيئة إعداد العروض جاهزة للعمل.",
      description: "أدر أعمال العرض من الاستقبال والحل والجوانب التجارية والمحتوى إلى الموافقات والتقديم والنتائج.",
      authority: "وصول متوافق مع دورك",
      foundationStatus: "عملك",
      foundationTitle: "مساحات العمل المصرح بها",
      foundationDescription: "اختر المجال المطابق لمسؤوليتك الحالية في العرض.",
      openFoundation: "فتح مساحة العمل",
      openTask: "فتح",
      noProposalTitle: "اختر نقطة البداية",
      noProposalDescription: "افتح مساحة عمل مصرحاً بها أعلاه، ثم اختر العرض أو المهمة التي تريد متابعتها.",
      governedModules: "وحدات",
      governedModulesDescription: "تعرض مساحة العمل المجالات والإجراءات المتاحة لدورك.",
      bilingualTitle: "العربية والإنجليزية",
      bilingualDescription: "يتبع الاتجاه والمحتوى المختلط والتواريخ والأرقام والعملات سياسة عرض صريحة.",
      accessibilityTitle: "إمكانية الوصول من الأساس",
      accessibilityDescription: "المعالم وتجاوز التنقل والتركيز المرئي والحالة الحية والحالات غير المعتمدة على اللون أسس قابلة لإعادة الاستخدام.",
      moduleBoundaryTitle: "مساحة العمل غير متاحة",
      moduleBoundaryDescription: "مساحة العمل هذه غير متاحة للمسار الحالي. ارجع إلى التنقل لمتابعة مهمة مصرح بها.",
    },
    opportunities: {
      eyebrow: "دورة حياة الفرصة المرجعية",
      title: "الفرص ونشاط العروض",
      description: "هويات ثابتة وإصدارات وأبعاد جاهزية وقرارات حالة محددة المسؤولية من سجل المجال.",
      emptyTitle: "لا توجد فرصة حتى الآن",
      emptyDescription: "أنشئ فرصة لبدء الاستقبال والتأهيل والتخطيط وإعداد العرض.",
      opportunityColumn: "الفرصة",
      customerColumn: "العميل",
      proposalColumn: "العرض",
      stateColumn: "حالة دورة الحياة",
      versionColumn: "الإصدار",
      updatedColumn: "آخر قرار",
      openActivity: "فتح سجل النشاط",
      detailEyebrow: "فرصة",
      customerLabel: "العميل",
      proposalLabel: "العرض",
      deadlineLabel: "الموعد النهائي",
      noDeadline: "غير مسجل",
      readinessTitle: "جاهزية مستقلة",
      readinessDescription: "يبقى كل بُعد إلزامي ظاهراً، ولا يمكن لدرجة مجمعة إخفاء أي عائق.",
      activityTitle: "سجل نشاط محدد المسؤولية",
      activityDescription: "قرارات الحالة والجاهزية واللقطات بترتيب زمني من دون محتوى مستندات أو بيانات تجارية محمية.",
      noActivity: "لا يتوفر نشاط للمجال.",
      actorLabel: "المنفذ",
      reasonLabel: "السبب",
      noReason: "لا تتطلب هذه النقلة سبباً",
      versionLabel: "إصدار السجل",
      createdAction: "تم إنشاء الفرصة",
      transitionedAction: "تم نقل دورة الحياة",
      readinessAction: "تم تحديث الجاهزية",
      snapshotAction: "تم إنشاء لقطة محفوظة",
      policyAction: "تم تفعيل سياسة دورة الحياة",
      unknownAction: "إجراء مجال",
      dimensions: {
        solution: "الحل",
        commercial: "التجاري",
        content: "المحتوى",
        compliance: "الامتثال",
        language: "اللغة",
      },
      readinessStatuses: {
        not_started: "لم يبدأ",
        in_progress: "قيد التنفيذ",
        blocked: "محظور",
        ready: "جاهز",
      },
      states: {
        draft: "مسودة",
        intake: "الاستقبال",
        qualification: "التأهيل",
        planning: "التخطيط",
        solutioning: "إعداد الحل",
        pricing: "التسعير",
        authoring: "التحرير",
        review: "المراجعة",
        approval: "الاعتماد",
        ready_to_submit: "جاهز للتقديم",
        submitted: "تم التقديم",
        amended: "معدل",
        won: "فوز",
        lost: "خسارة",
        withdrawn: "مسحوب",
        expired: "منتهي",
        archived: "مؤرشف",
      },
    },
    documents: {
      eyebrow: "استقبال أدلة",
      title: "سجل مستندات العرض",
      description: "ارفع أدلة العميل والمورّد إلى حجر معزول. يحتفظ كل ملف مقبول بإصدار غير قابل للتغيير وسلسلة تدقيق.",
      backToOpportunity: "العودة إلى الفرصة",
      uploadTitle: "رفع الأدلة",
      uploadDescription: "تُحجر الملفات وتُفحص من البرمجيات الضارة ويُكتشف نوعها وتُطبق عليها السياسة قبل إتاحة الوصول.",
      fileLabel: "اختر ملفاً",
      dropHint: "أفلت ملفاً هنا أو استخدم محدد الملفات المتاح بلوحة المفاتيح.",
      categoryLabel: "فئة المستند",
      sensitivityLabel: "الحساسية",
      issuerLabel: "الجهة المصدرة (اختياري)",
      effectiveDateLabel: "تاريخ السريان (اختياري)",
      replacementLabel: "استبدال / إصدار جديد (اختياري)",
      newDocument: "إنشاء مستند جديد",
      uploadAction: "رفع آمن",
      uploading: "جارٍ الرفع إلى الحجر",
      uploadSuccess: "قُبل الملف للمعالجة.",
      uploadFailure: "تعذر قبول الملف. راجع الملف وحاول مرة أخرى.",
      registerTitle: "سجل المستندات",
      emptyTitle: "لا توجد أدلة مرفوعة للعرض",
      emptyDescription: "اختر ملفاً مصرحاً به لبدء دورة حياة الأدلة.",
      documentColumn: "المستند",
      categoryColumn: "الفئة",
      ownerColumn: "المالك",
      sensitivityColumn: "الحساسية",
      statusColumn: "حالة المعالجة",
      versionColumn: "الإصدار",
      actionsColumn: "الإجراءات",
      previewAction: "معاينة",
      downloadAction: "تنزيل",
      retryAction: "إعادة المعالجة",
      activityAction: "عرض النشاط",
      refreshAction: "تحديث السجل",
      reviewReasonLabel: "سبب المراجعة البشرية",
      promoteAction: "اعتماد وترقية",
      rejectAction: "رفض الملف",
      actionFailure: "تعذر إكمال الإجراء.",
      noActivity: "لا يتوفر نشاط.",
      systemActor: "عامل النظام",
      versionPrefix: "الإصدار",
      sizeLabel: "الحجم",
      reasonCodeLabel: "رمز السبب",
      intelligenceAction: "مراجعة الاستخراج",
      intelligenceTitle: "مراجعة استخراج الأدلة",
      intelligenceDescription: "يُفضل النص الأصلي ويُستخدم OCR عند الحاجة فقط. تبقى كل نتيجة غير متحققة حتى يتخذ الإنسان قراراً.",
      intelligenceLoading: "جارٍ تحميل أدلة الاستخراج",
      intelligenceEmpty: "لم يبدأ الاستخراج. حدّث بعد اكتمال معالجة المستند.",
      intelligenceStatusLabel: "حالة الاستخراج",
      intelligenceStatuses: { not_started: "لم يبدأ", queued: "في قائمة الانتظار", running: "قيد التنفيذ", succeeded: "مكتمل", failed: "تمت جدولة إعادة المحاولة", dead_letter: "يتطلب استعادة يدوية" },
      extractionModeLabel: "الطريقة",
      languageLabel: "اللغة المكتشفة",
      classificationLabel: "التصنيف المقترح",
      qualityLabel: "درجة الجودة",
      confidenceLabel: "الثقة",
      runLabel: "تشغيل الاستخراج",
      unverifiedBannerTitle: "قيم مرشحة غير متحققة",
      unverifiedBannerDescription: "لا يستطيع الاستخراج أو الذكاء الاصطناعي التحقق من مخرجاته أو تحديث حقيقة العرض. أكد القيم المؤثرة مقابل المصدر المشار إليه.",
      textEvidenceTitle: "أدلة نصية محددة الموقع",
      tablesTitle: "الجداول المستخرجة",
      candidatesTitle: "القيم المرشحة المنظمة",
      candidateKindColumn: "نوع القيمة",
      candidateValueColumn: "القيمة المستخرجة",
      sourceColumn: "المصدر الدقيق",
      openSourceAction: "افتح المصدر المستشهد به",
      verificationColumn: "التحقق",
      unverifiedStatus: "غير متحقق",
      noCandidates: "لم يُعثر على قيم منظمة. لا يُعد ذلك دليلاً على اكتمال البيانات المطلوبة.",
      injectionTitle: "تم اكتشاف تعليمات غير موثوقة",
      injectionDescription: "عوملت التعليمات الموجودة داخل الملف المرفوع كنص مستند ولم تتمكن من تغيير قواعد الوصول أو المعالجة.",
      aiBoundaryTitle: "حالة معالجة الذكاء الاصطناعي",
      aiManualFallback: "المسار اليدوي البديل نشط",
      aiBoundaryDescription: "اقتراحات الذكاء الاصطناعي للقراءة فقط وتتطلب مراجعة بشرية قبل الاستخدام. ولا يمكنها اعتماد العمل أو تغيير حالة العرض.",
      retryExtractionAction: "إنشاء إصدار استخراج جديد",
      retryExtractionReasonLabel: "سبب إعادة الاستخراج",
      retryExtractionReasonPlaceholder: "اشرح مشكلة المحلل أو OCR أو جودة المصدر",
      sourcePage: "صفحة",
      sourceNotFound: "موضع المصدر الدقيق غير موجود في إصدار الاستخراج هذا.",
      sourceSheet: "ورقة",
      sourceSlide: "شريحة",
      sourceParagraph: "فقرة",
      sourceLine: "سطر",
      methodLabels: { native: "نص أصلي", ocr: "تعرف ضوئي", hybrid: "نص أصلي + تعرف ضوئي" },
      languageLabels: { en: "الإنجليزية", ar: "العربية", mixed: "العربية والإنجليزية", unknown: "غير معروفة" },
      categories: {
        rfp: "طلب عرض RFP", rfq: "طلب تسعير RFQ", rfi: "طلب معلومات RFI", questionnaire: "استبيان", clarification: "استيضاح",
        meeting_outcome: "مخرجات اجتماع", quotation: "عرض سعر", technical: "فني", commercial: "تجاري",
        legal: "قانوني", compliance: "امتثال", supporting_evidence: "أدلة داعمة", other: "أخرى",
      },
      sensitivities: { internal: "داخلي", confidential: "سري", restricted: "مقيد" },
      statuses: {
        quarantined: "في الحجر", scanning: "قيد الفحص", accepted: "مقبول", review_required: "يتطلب مراجعة بشرية",
        rejected: "مرفوض", processing_failed: "فشلت المعالجة", dead_letter: "يتطلب استعادة يدوية",
      },
    },
    foundation: {
      eyebrow: "مرجع داخلي للمكونات",
      title: "أساس واجهة ثنائية اللغة",
      description: "مكونات إنتاجية مكيفة من اللغة البصرية المعتمدة في ResolveOps لمسارات عمل إعداد العروض.",
      breadcrumbWorkspace: "مساحة العمل",
      breadcrumbCurrent: "أساس الواجهة",
      examplesNotice: "القيم أدناه أمثلة واجهة منقحة وليست سجلات عروض أو موافقات.",
      actionsTitle: "الإجراءات والتغذية الراجعة",
      primaryAction: "إجراء رئيسي",
      secondaryAction: "إجراء ثانوي",
      openDialog: "فتح مربع المراجعة",
      openDrawer: "فتح درج الأدلة",
      showNotification: "إظهار إشعار",
      successStatus: "جاهز",
      warningStatus: "بحاجة إلى مراجعة",
      errorStatus: "محظور",
      infoBannerTitle: "تبقى الأدلة هي المرجع",
      infoBannerDescription: "لا تستبدل ملخصات الواجهة مستندات المصدر أو حالة الأعمال المعتمدة.",
      warningBannerTitle: "المراجعة البشرية مطلوبة",
      warningBannerDescription: "تبقى نصائح الذكاء الاصطناعي والقيم المستخرجة غير متحققة حتى يعتمدها شخص مخول.",
      tabsLabel: "أمثلة الأساس",
      overviewTab: "نظرة عامة",
      evidenceTab: "الأدلة",
      activityTab: "النشاط",
      tabOverviewCopy: "تحافظ مساحة العمل الثابتة على الهوية والحالة والموعد والجاهزية ضمن السياق.",
      tabEvidenceCopy: "تبقى المصادر قابلة للوصول ومميزة بصرياً عن النصائح وقرارات الاعتماد.",
      tabActivityCopy: "تحتفظ التغييرات والتعليقات والقرارات وأحداث التدقيق بالمسؤولية المحددة.",
      formTitle: "نموذج مترجم",
      proposalNameLabel: "اسم العرض",
      proposalNameHint: "استخدم اسم الفرصة الموجه للعميل.",
      proposalNamePlaceholder: "تحديث الخدمات الإقليمية",
      requiredError: "أدخل اسم العرض قبل المتابعة.",
      tableTitle: "جدول متطلبات متجاوب",
      requirementColumn: "المتطلب",
      sourceColumn: "المصدر",
      ownerColumn: "المالك",
      statusColumn: "الحالة",
      rowRequirement: "توفير وثائق خدمة ثنائية اللغة",
      rowSource: "RFP § 4.2",
      rowOwner: "فريق الحلول",
      rowStatus: "بحاجة إلى مراجعة",
      mobileRecordLabel: "تفاصيل المتطلب",
      emptyTitle: "لا توجد أدلة مرفقة",
      emptyDescription: "ارفع مستنداً مصدرياً لبدء معالجة الأدلة ومراجعتها.",
      errorStateTitle: "المحتوى غير متاح",
      errorStateDescription: "لا تكشف حالة الخطأ الآمنة محتوى العميل أو المعرفات الداخلية.",
      loadingLabel: "جارٍ تحميل محتوى مساحة العمل",
      dialogTitle: "المراجعة مطلوبة",
      dialogDescription: "تحقق من أن التفاعل التوضيحي يحافظ على التركيز والتشغيل بلوحة المفاتيح.",
      dialogConfirm: "تأكيد المراجعة",
      cancelAction: "إلغاء",
      closeAction: "إغلاق",
      drawerTitle: "سياق الأدلة",
      drawerDescription: "يبقى الوصول إلى المصدر منفصلاً عن نصائح الذكاء الاصطناعي وحالة الاعتماد.",
      notificationMessage: "تم الإعلان عن إشعار الواجهة.",
      notificationRegion: "الإشعارات",
      proposalExampleLabel: "مثال واجهة",
      proposalExampleName: "تحديث الخدمات الإقليمية",
      proposalExampleStatus: "مثال مسودة",
      proposalExampleDeadline: "موعد التقديم",
      proposalExampleOwner: "مالك العرض",
      proposalExampleOwnerValue: "فريق العروض",
      proposalExampleVersion: "الإصدار النشط",
      proposalExampleVersionValue: "المسودة 1",
      proposalStagesLabel: "مراحل العرض",
      stageIntake: "الاستقبال",
      stageRequirements: "المتطلبات",
      stageSolution: "الحل",
      stageCommercials: "الجوانب التجارية",
      stageContent: "المحتوى",
      stageApprovals: "الموافقات",
      stageSubmission: "التقديم",
      blockersLabel: "العوائق المفتوحة",
      blockersValue: "عائقان توضيحيان",
      advisorTitle: "مستشار XcutIQ لذكاء العروض",
      advisorStatus: "استشاري فقط",
      advisorDescription: "تُفصل النصائح بصرياً عن الحقائق والحسابات والموافقات وتتطلب دائماً قراراً بشرياً.",
      advisorAction: "مراجعة حدود الاستشارة",
      mainTaskTitle: "سطح المهمة الرئيسي",
      mainTaskDescription: "أكمل مهمة العرض التالية باستخدام مساحات العمل المتاحة لدورك.",
    },
  },
} as const;

export type Messages = (typeof messages)[Locale];

export function messagesFor(locale: Locale): Messages {
  return messages[locale];
}

export function replaceLocaleInPath(pathname: string, locale: Locale): string {
  const segments = pathname.split("/");
  if (segments[1] && isLocale(segments[1])) segments[1] = locale;
  else segments.splice(1, 0, locale);
  return segments.join("/") || `/${locale}`;
}
